import { Injectable, TemplateRef } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

/**
 * Service to manage dynamic header content based on scroll position.
 *
 * This service works with the BpScrollHeaderDirective to show content
 * in the app header when the original element scrolls out of view.
 *
 * @example
 * // In your component template:
 * <div [bpScrollHeader]="headerTemplate">
 *   <img [src]="goal?.imageLarge" />
 *   <span>{{ goal?.title }}</span>
 * </div>
 *
 * <ng-template #headerTemplate>
 *   <div class="flex items-center gap-2">
 *     <img [src]="goal?.imageLarge" class="w-8 h-8 rounded-full" />
 *     <span>{{ goal?.title }}</span>
 *   </div>
 * </ng-template>
 */
@Injectable({
  providedIn: 'root',
})
export class BpScrollHeaderService {
  private _scrollContent = new BehaviorSubject<TemplateRef<any> | null>(null);
  scrollContent$ = this._scrollContent.asObservable();

  private _isVisible = new BehaviorSubject<boolean>(false);
  isVisible$ = this._isVisible.asObservable();

  /**
   * Set the template to display in the header when the observed element is out of view
   * @param template The TemplateRef to display in the header
   */
  setScrollContent(template: TemplateRef<any>): void {
    this._scrollContent.next(template);
    this._isVisible.next(true);
  }

  /**
   * Clear the scroll-based header content and restore default header
   */
  clearScrollContent(): void {
    this._scrollContent.next(null);
    this._isVisible.next(false);
  }

  /**
   * Check if scroll content is currently visible
   */
  get isVisible(): boolean {
    return this._isVisible.getValue();
  }
}
